import { ALERT_TOPICS, type LiveTopic } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT EACH SCREEN DEPENDS ON. The dependency map, in one file.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C21 · `decisions.md` R-19. A change arrives as a topic; a screen refetches if it reads anything
 * that topic can affect. **Nothing else in the app states these relationships**, and they are not
 * obvious from the code: that the project dashboard depends on `worklog` is true because of a SQL
 * aggregate three layers away.
 *
 * ⚠️ **Named sets rather than inline arrays, because each is used TWICE** — once in `routes.tsx`,
 * where the route declares what it depends on and a gate enforces that it declares something, and
 * once in the screen, which passes it to `useLiveInvalidation`. Two inline arrays would drift, and
 * the drift is invisible: the route table would claim a dependency the screen does not act on.
 *
 * ⚠️ **A topic here is COARSE on purpose.** `worklog` means "some worklog in this workspace
 * changed", not "the one you are looking at". Finer filtering belongs to the refetch, which is
 * scoped and permission-trimmed exactly as it always is; filtering here would mean the client
 * deciding what it is allowed to know about, which is the thing R-19 refuses.
 */

/**
 * The vocabularies every screen renders — a status chip, a priority dot, a metafield value.
 *
 * ⚠️ Included almost everywhere, and that is correct rather than lazy: renaming a status changes
 * what every list, board and detail draws, and a screen that missed it shows a name nobody uses
 * any more with no way to tell it is stale.
 */
export const VOCABULARY: readonly LiveTopic[] = ['task_status', 'task_priority', 'metafield'];

/**
 * C24 · contracts and their line items.
 *
 * ⚠️ **`task` and `worklog` are in here because every figure on a contract is a join through
 * them.** A contract's tracked hours are somebody else's worklog on somebody else's task; a line
 * item's task count changes when a task is assigned to it from a screen this one cannot see.
 * Without those two the list would sit there showing yesterday's totals with no way to tell.
 */
export const CONTRACT_TOPICS: readonly LiveTopic[] = [
  'contract',
  'lineitem',
  /**
   * C56 · ⚠️ **External costs, for the reason the note above already gives.** A line item's
   * money is a sum over rows that live somewhere else, exactly like its tracked hours — and a
   * licence renewed or a subcontractor's invoice filed changes what a deliverable has cost with no
   * `contract.*` event to wait for.
   *
   * ⚠️ It arrives one commit before the section that lists them, because the endpoints are audited
   * and announced now and a topic nothing listens for is a change delivered and dropped — which
   * `live.test.ts` refuses, correctly.
   */
  'extcost',
  /**
   * C57 · ⚠️ **Invoices, for the third time the same argument.** *Kiszámlázva* and *Hátralévő* on a
   * line item and on a period are sums over rows that live in another table — somebody else issuing
   * an invoice changes what this deliverable has left to bill, with no `contract.*` event to wait
   * for.
   */
  'invoice',
  'task',
  /* 30 Oct 2026 · EV-2 · the status weights earned value is counted by — and a status's category. */
  'task_status',
  'worklog',
  'project',
  'member',
];

/** Time: an entry, the rules that bound it, and the person it belongs to. */
export const TIME_TOPICS: readonly LiveTopic[] = ['worklog', 'task', 'project', 'member'];

/**
 * Tasks, and everything that changes what a task row shows.
 *
 * ⚠️ `worklog` is in here because a task carries tracked hours, and `project` because it carries
 * the project's name and colour.
 */
export const TASK_TOPICS: readonly LiveTopic[] = [
  'task',
  'worklog',
  'project',
  'member',
  /* ⚠️ C22 · an attachment somebody else adds to a comment you are reading, and — the case with
   * no request behind it — your own upload leaving `pending` once the scan finishes. */
  'file',
  /* ⚠️ C36 · the task's *Linked issue* row. A sync that links this task to a remote issue is an
     `integration.*` event, so without this the row stays absent until somebody reloads. */
  'integration',
  ...VOCABULARY,
];

/**
 * Projects and clients.
 *
 * ⚠️ **`worklog` and `task` are the point of this one.** The product owner's example was *"updates
 * on projects based on logs"*: a project's logged hours, its task counts and its doneness are all
 * aggregates over rows that live somewhere else, so the screen has no `project.*` event to wait
 * for. Leaving them out would produce exactly the bug being fixed — a dashboard that only updates
 * when somebody edits the project itself.
 */
export const DELIVERY_TOPICS: readonly LiveTopic[] = [
  'project',
  'client',
  /* 21 Oct 2026 · a client's contacts are edited in the client dialog, and were listened for only
     by the deal screens — which `DEALS_ENABLED` has taken away. */
  'contact',
  'task',
  'worklog',
  'member',
  /* C31 · ⚠️ **The project detail renders role NAMES and COLOURS**, so a rename in settings has to
     reach it — the same reason `VOCABULARY` is in here at all. */
  'projectrole',
  /**
   * C52 · ⚠️ **The resource plan, and it belongs here for the reason the header already gives.**
   * A project's PLANNED hours are an aggregate over rows that live somewhere else, exactly like its
   * logged hours and its task counts — so the screen has no `project.*` event to wait for when
   * somebody types a cell into the planner, and leaving it out reproduces the very bug the note
   * above was written about.
   *
   * ⚠️ It arrived here one batch before the project's Erőforrás tab that draws the figures, because
   * the allocation endpoints were audited and announced first and a topic nothing listens for is a
   * change delivered and dropped — which `live.test.ts` refuses, correctly.
   */
  'allocation',
  ...VOCABULARY,
];

/**
 * The workspace calendar — C42.
 *
 * ⚠️ **One topic, and it is the whole set.** The screen reads holidays and nothing else; the
 * workspace's country rides along on a second request whose only consumer is a footer label, and
 * re-reading the calendar because somebody renamed the workspace would be noise.
 */
export const CALENDAR_TOPICS: readonly LiveTopic[] = ['holiday'];

/**
 * Work schedules — C43.
 *
 * ⚠️ **One topic for both nouns.** A template and an assignment are two tables and one screen, and
 * every `schedule.*` action changes something that screen draws.
 */
export const SCHEDULE_TOPICS: readonly LiveTopic[] = ['schedule'];

/**
 * The Erőforrás grid — C53.
 *
 * ⚠️⚠️ **FOUR TOPICS, AND THREE OF THEM ARE NOT ALLOCATIONS.** The grid's rows are people and
 * its *Munkaidő* line is C44's kernel, so a schedule changed in settings, a holiday imported or a
 * leave request approved each move a figure on it — and a planner reading a capacity that a
 * colleague has already invalidated is the failure this whole mechanism exists to prevent. The same
 * argument `DELIVERY_TOPICS` makes for `worklog`.
 */
export const RESOURCE_TOPICS: readonly LiveTopic[] = [
  'allocation',
  'schedule',
  'holiday',
  'leave',
  'member',
];

/**
 * Money — C51. ⚠️ **Both topics on both screens**, because the two read each other: a rate card
 * period is priced in a currency, and the currency screen refuses to delete one that a card uses.
 * A screen that watched only its own half would show a stale refusal.
 */
export const MONEY_TOPICS: readonly LiveTopic[] = ['currency', 'ratecard'];

/**
 * Leave — C46, and C47's requests when they arrive.
 *
 * ⚠️ **One topic for the types, the quotas and the per-person flag**, because all three are read
 * by one screen and a quota is meaningless without the type it belongs to.
 */
export const LEAVE_TOPICS: readonly LiveTopic[] = ['leave'];

/** Overtime — C48. Types and the claims raised against them, on one screen each. */
export const OVERTIME_TOPICS: readonly LiveTopic[] = ['overtime'];

/**
 * Skills — C54. The vocabulary and who holds what in it.
 *
 * ⚠️ **One topic for all four tables**, for `LEAVE_TOPICS`' reason and more so: a category
 * renamed changes the dot on every skill row, a level reordered re-sorts every person's list, and
 * no surface reads one of the four without the others.
 */
export const SKILL_TOPICS: readonly LiveTopic[] = ['skill'];

/** R-60 · the team vocabulary — the settings list and every filter that offers a team. */
export const TEAM_TOPICS: readonly LiveTopic[] = ['team'];

/** The home screen is a personal summary over everything the caller touches. */
export const DASHBOARD_TOPICS: readonly LiveTopic[] = [
  'worklog',
  'task',
  'project',
  /* C49 · the Logolás grid reads C44's kernel, so a holiday imported or a schedule changed moves
     every cell in it; and the Szabadságok timeline is C47's requests. Without these three the two
     dashboards would only update when somebody logged an hour. */
  'holiday',
  'schedule',
  'leave',
  ...VOCABULARY,
];

/**
 * 16 Oct 2026 · the caller's own week of allocations (`/dashboards/my-week`) — the resource grid's
 * row, plus what was logged against each line item, so a worklog moves it too.
 */
export const MY_WEEK_TOPICS: readonly LiveTopic[] = [...RESOURCE_TOPICS, 'worklog'];

/**
 * 16 Oct 2026 · Kezdőlap, reworked: the summary, the *Túlóra* card's claims and *Allokációim*.
 * ⚠️ Its own set rather than more of `DASHBOARD_TOPICS`, which the Logolás and Szabadságok
 * dashboards also declare and which has no reason to wake them for an overtime claim.
 */
export const HOME_TOPICS: readonly LiveTopic[] = [
  ...new Set<LiveTopic>([...DASHBOARD_TOPICS, 'overtime', ...MY_WEEK_TOPICS]),
];

/** People: the roster, and what each person's row summarises. */
export const PEOPLE_TOPICS: readonly LiveTopic[] = [
  'member',
  /* R-60 · a renamed team is a changed cell on every person in it. */
  'team',
  'role',
  'worklog',
  'task',
  'project',
  ...VOCABULARY,
];

/**
 * Reports.
 *
 * ⚠️ A saved report is a definition plus a snapshot over the data it aggregates, so it moves when
 * the data moves — not only when somebody edits the report. `report.*` exists because a colleague
 * can create or refresh one while you are looking at the list.
 */
export const REPORT_TOPICS: readonly LiveTopic[] = [
  'report',
  'worklog',
  'task',
  'project',
  'client',
];

/**
 * 16 Oct 2026 · a LIST report's rows. ⚠️ Wider than `REPORT_TOPICS`: a list is never stored, so
 * every row it shows is live data — contracts and line items included, for the money sources.
 */
export const REPORT_LIST_TOPICS: readonly LiveTopic[] = [
  'report',
  'worklog',
  'task',
  'project',
  'client',
  'contract',
  'lineitem',
  'invoice',
  'member',
];

/**
 * 17 Oct 2026 · a TIMELINE report — everything a list reads, and what moves a working day:
 * schedules, holidays, leave and people (the Munkaidő source, `report-timeline.ts`).
 */
export const REPORT_TIMELINE_TOPICS: readonly LiveTopic[] = [
  ...new Set<LiveTopic>([...REPORT_LIST_TOPICS, 'schedule', 'holiday', 'leave', 'member']),
];

/**
 * The sidebar's counts and favourites.
 *
 * ⚠️ The counts are the most visible live surface in the product — a number in the rail that
 * disagrees with the list beside it is the complaint this whole chunk exists to remove.
 */
export const SIDEBAR_TOPICS: readonly LiveTopic[] = [
  'task',
  'project',
  'client',
  'worklog',
  'member',
  'report',
];

/**
 * 28 Oct 2026 · the *Idő* row's marks (`/dashboards/logging/me`): what the reader logged, and the
 * three things that move what a day expected of them.
 */
export const MY_LOGGING_TOPICS: readonly LiveTopic[] = [
  'worklog',
  'task',
  'leave',
  'holiday',
  'schedule',
];

/** The notification bell. C17 polled once a minute; C21 is why it no longer has to. */
export const NOTIFICATION_TOPICS: readonly LiveTopic[] = ['task', 'worklog', 'member', 'project'];

/**
 * Which extensions this workspace has switched on.
 *
 * ⚠️ **`extension` is a topic somebody else's tab produces.** Turning one on is a workspace-wide
 * act: an admin enables Jira in another window and this list has to notice, or two people spend a
 * minute disagreeing about whether it is on. `member` is here because the integrations screen names
 * who enabled it, and a renamed person would otherwise keep their old name.
 *
 * ⚠️ **Named `EXTENSION_TOPICS` since item 68**, because the marketplace it was named after is
 * gone — the install is a toggle on the integrations page now (D7).
 */
export const EXTENSION_TOPICS: readonly LiveTopic[] = ['extension', 'member'];

/**
 * C33 · the integration screens.
 *
 * ⚠️ **`extension` is in here as well as `integration`.** An instance can only exist for an
 * installed extension, so an admin uninstalling Jira in another tab has to reach the catalogue and
 * the instance list — and `member` because both maps print a member's name.
 */
export const INTEGRATION_TOPICS: readonly LiveTopic[] = ['integration', 'extension', 'member'];

/**
 * C36 · one instance, which also holds the parked-hours inbox.
 *
 * ⚠️ **`worklog` on top, and it is not decoration.** Adopting a parked hour files it as a *worklog*
 * write, so a colleague pairing an identity in People management empties rows out of this inbox —
 * and without the topic the person watching it would still see them waiting.
 */
export const INTEGRATION_INSTANCE_TOPICS: readonly LiveTopic[] = [
  ...INTEGRATION_TOPICS,
  'worklog',
  /* J-3 · the unrouted tab: a rule saved on a contract files tasks out of it. */
  'task',
];

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · the Alertek tab. Fourteen topics, and the server reads the same 14. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A RE-EXPORT OF `ALERT_TOPICS`, NOT A SECOND LIST.** The same fourteen decide two things:
 * what makes a project's findings STALE on the server (P5-4, in `alert.service.ts`) and what makes
 * this tab refetch. Two copies would drift, and the drift would be the worst kind — the screen
 * subscribed to thirteen of the fourteen would quietly stop updating for exactly one kind of
 * change, which nobody would find by reading either list.
 *
 * ⚠️ **`LiveTopic` and `AlertTopic` are separate unions and this is where they are checked to
 * agree.** The assignment below is the whole check: a topic the alerts depend on that the live
 * channel cannot deliver fails the typecheck here rather than being silently never announced.
 */
/**
 * ⚠⚠ **PLUS `alert` ITSELF, WHICH `ALERT_TOPICS` DELIBERATELY OMITS.** That list answers *what
 * makes a project's findings STALE*, and a scan's own `alert.scan` entry must not be on it or
 * every scan would immediately make the project stale again — a loop, one read apart.
 *
 * ⚠️ **The screen's question is the other one: *what should make this tab refetch*.** Somebody
 * else waiving a finding writes `alert.dismiss` and changes nothing else, so without this topic an
 * open tab would keep showing it as active until an unrelated task moved.
 */
export const ALERT_SCREEN_TOPICS: readonly LiveTopic[] = [...ALERT_TOPICS, 'alert'];

/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE PROJECT DETAIL CARRIES TWO ALERT FIGURES NOW, SO IT LISTENS FOR THEM.  │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * `openAlertCount` on the tab badge and `riskScore` on the header pill both ride the project row,
 * and both change when a finding opens, resolves or is waived — none of which any of
 * `DELIVERY_TOPICS` announces. So fixing a fault left the badge reading the old number until
 * something unrelated happened to the project, which is what the 21 Sep testing round caught.
 *
 * ⚠️ **`alert` alone on top of the delivery set**, not the whole scan dependency list: the
 * project row does not recompute because a rate card changed, it recomputes because the SCAN
 * wrote a finding — and the scan announces itself.
 */
export const PROJECT_DETAIL_TOPICS: readonly LiveTopic[] = [
  ...DELIVERY_TOPICS,
  'alert',
  /* P2-32 · ⚠️ the gate's two counts ride the project row too, so the start state must lift the
     moment the contract or line item it asked for exists — including one made on another tab. */
  'contract',
  'lineitem',
];

/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · what each record's own History tab listens for.                   │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * The tab sits inside a screen that is already live, but the history is its OWN read
 * (`/history/:type/:id`), so it subscribes for itself rather than waiting for the host to remount
 * it. Each set is the topics whose actions write the record's audit rows: a project's history is its
 * own, its members', allocations', costs', invoices' and contracts' (U-9); a person's includes their
 * project memberships.
 *
 * ⚠️ **The workspace audit log is NOT here** — it changes on every write in the workspace, so it
 * refreshes on request (U-8) and `routes.tsx` says so in its `notLiveBecause`.
 */
export const ENTITY_HISTORY_TOPICS = {
  task: ['task', 'worklog'],
  project: ['project', 'allocation', 'extcost', 'invoice', 'contract', 'lineitem'],
  contract: ['contract', 'lineitem'],
  line_item: ['lineitem'],
  invoice: ['invoice'],
  external_cost: ['extcost'],
  person: ['member', 'project'],
  leave_request: ['leave'],
  overtime_request: ['overtime'],
} as const satisfies Readonly<Record<string, readonly LiveTopic[]>>;

export type EntityHistoryType = keyof typeof ENTITY_HISTORY_TOPICS;

/**
 * Phase 6 · every deal screen. ⚠️ `client` and `member` because a row shows the company's name and
 * colour and the owner's; `projectrole` because demand and placeholders are drawn by role; `project`
 * and `contract` because a generation makes both and the record lists what it made.
 */
export const DEAL_TOPICS: readonly LiveTopic[] = [
  'deal',
  'contact',
  'client',
  'member',
  'projectrole',
  'project',
  'contract',
];
